import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PostsData } from '../../form-data/post-data';
import { Post, newPost, Comment } from '../../interfaces/post.interface';
import { DomSanitizer } from '@angular/platform-browser';
import { SocketService } from '../../services/socket.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollaborationService } from '../../services/collaboration.service';
import { DocumentsSpace, PostsSpace, User, WeavyData } from '../../interfaces/space.interface';
import { BasicReusableComponentComponent } from '../basic-reusable-component/basic-reusable-component.component';
import { assetUrl } from '../../asset-url';
import { lastValueFrom } from 'rxjs';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { UnAuthorizedComponent } from '../../components/un-authorized/un-authorized.component';
import { NotificationSettingsComponent } from '../../components/notification-settings/notification-settings.component';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { QuillRTEComponent } from '../../components/quill-rte/quill-rte.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PostDetailsComponent } from '../../components/post-details/post-details.component';
import { AssetUrlPipe } from '../../pipes/asset-url.pipe';

@Component({
    selector: 'app-posts',
    templateUrl: './posts.component.html',
    styleUrl: './posts.component.scss',
    imports: [UnAuthorizedComponent, NotificationSettingsComponent, NgClass, MatTooltip, QuillRTEComponent, SpinnerComponent, PostDetailsComponent, AssetUrlPipe]
})
export class PostsComponent extends BasicReusableComponentComponent implements OnInit, OnDestroy {
  @Input() declare weavyData:WeavyData;
  showPostDetails = false;
  selectedPost: any;
  selectedPostId: any;
  subscribe: boolean = false;
  control: FormControl = new FormControl();
  posts: Post[] = [];
  loggedInUser!: User;
  isFullScreen: boolean = false;
  isUserUnAuthorized = false;
  errorStatusCode;
  displayName = "Posts";
  @Output() downloadAdditionalDocs = new EventEmitter<any>();
  isApplicationRunningOnOnItSystem = false;
  isPostRender: boolean = false;
  constructor(public dialog: MatDialog, public sanitizer: DomSanitizer, private snackBar: MatSnackBar, public socketService: SocketService, private renderer: Renderer2, override collaborationService: CollaborationService) {
  super(collaborationService);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.isApplicationRunningOnOnItSystem = this.collaborationService.isApplicationRunningOnOnItSystem;
  }

  @HostListener('window:beforeunload')
  onWindowClose(): boolean {   
    return this.checkIsEditorDataModified();
  }

  checkIsEditorDataModified() {
   const editorDataModified = Object.keys(this.collaborationService.editorDataModified).some(key => this.collaborationService.editorDataModified[key]);
    if (editorDataModified) {
      return false;
    } else {
      return true;
    }
  }

  refreshPosts(ignorePostDetailsView: boolean) {
    (window as any).dataLayer.push({
      event: "referral_link_click",
      eventLabel: 'Refresh Posts',
      tabName:'Post comments',
      eventPage: "Referral Actions",
    });
    const checkIsEditorDataChanged = !this.checkIsEditorDataModified();
    if (checkIsEditorDataChanged) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: "Unsaved changes",
          message: "You have made changes to one or more messages that haven't been saved. Are you sure you want to proceed without saving them?",
          buttonText: "ok",
          cancelButtonText: "cancel"
        },
        autoFocus: false
      }).afterClosed().subscribe((res) => {
        if (res) {
          this.collaborationService.clearRTEData.next(true)
          this.initialize(ignorePostDetailsView);
        }
      });
    } else {
      this.initialize(ignorePostDetailsView);
    }
  }
  
  override initialize(ignorePostDetailsView = false) {    
    this.showLoader = true;
    if(!ignorePostDetailsView) {
      this.initiateRequiredAppsSpaces()
    }
    this.collaborationService.postsWeavyData = this.weavyData;
    const splitKey = window.location.href.includes("weavy_navigate=") ? "weavy_navigate=" :"weavy_navigate/"; 
    const postDataFromQueryParams = window.location.href.split(splitKey)[1] ?? "";    
    this.selectedPostId = postDataFromQueryParams ? postDataFromQueryParams.split("&")[0]  : "";
    const objForInitializeCall = this.collaborationService.getObjForInitializeCall(this.weavyData);
    this.collaborationService.initializePosts(objForInitializeCall,ignorePostDetailsView).subscribe( {
      next:  (response: PostsSpace) => { 
        this.isPostRender = true;       
        if(!response) {
          this.showLoader = false;
          return;
        }
        this.collaborationService.additionalDataPulledInTabs[objForInitializeCall.appKey] = response;   
        this.collaborationService.allPostSpaceDetails = response;
        
        if(!this.weavyData?.isIhubScreen && this.collaborationService.allPostSpaceDetails && this.collaborationService.allPostSpaceDetails.appName) {
            this.displayName = this.weavyData.draft? `Draft~${this.collaborationService.allPostSpaceDetails.appName}`: (this.collaborationService.allPostSpaceDetails.appName ?? 'Other')
        }
        this.subscribe = response.spaceFollowed;        
        this.collaborationService.spaceId = response.spaceId;
        this.loggedInUser = this.collaborationService.allPostSpaceDetails.user;
        // this.posts = await Promise.all(response.posts.map(post => this.getPostDataForRendering(post)));
        this.posts = [];
        for (const post of response.posts) {
          const postData =  this.getPostDataForRendering(post);
          this.posts.push(postData);
        }
        if(this.selectedPostId && !ignorePostDetailsView) {
          this.selectedPost = this.posts.find((post: Post) => post.postId == this.selectedPostId);
          if(this.selectedPost) {
            this.showPostDetails = true;
          }
        }      
        this.showLoader = false;
      },
     error: (err: any) => {
      this.isUserUnAuthorized = true;
      this.errorStatusCode = err.status;
      this.collaborationService.displayErrorMessage(err,'Error while retrieving Posts. Please try after some time.');
      this.showLoader = false;
    }
    });
  }

  initiateRequiredAppsSpaces() {
    if (this.weavyData.referralDocumentsObj) {       
        const objForInitializeCall = this.collaborationService.getObjForInitializeCall(this.weavyData.referralDocumentsObj);
        this.collaborationService.initDocuments({ ...objForInitializeCall, tab: "referral" }).subscribe({
          next: (res: DocumentsSpace) => {
            this.collaborationService.additionalDataPulledInTabs[objForInitializeCall.appKey] = res;            
          },
          error: (err: any) => {
            this.isUserUnAuthorized = true;
            this.errorStatusCode = err.status;           
            this.collaborationService.displayErrorMessage(err, 'Error while retrieving documents. Please try after some time.');
          }
        })
    }
    if (this.weavyData.policyDocumentsObj) {      
      const objForInitializeCall = this.collaborationService.getObjForInitializeCall(this.weavyData.policyDocumentsObj);
      this.collaborationService.initDocuments({ ...objForInitializeCall, tab: "policy" }).subscribe({
        next: (res: DocumentsSpace) => {
          this.collaborationService.additionalDataPulledInTabs[objForInitializeCall.appKey] = res;         
        },
        error: (err: any) => {
          this.isUserUnAuthorized = true;
          this.errorStatusCode = err.status;        
          this.collaborationService.displayErrorMessage(err, 'Error while retrieving documents. Please try after some time.');
        }
      })
    }
  }

  listenToSocketConnection() {
    if (this.socketService.allPostsSubscriptions.length > 0) {
      this.socketService.unsubscribeFromTopics(this.socketService.allPostsSubscriptions);
    }
    this.socketService.isSocketConnected.subscribe((res: any) => {
      if (res) {
        this.socketService.getAllPosts();
      } else {
        this.showLoader = true;
      }
    })
  }

  ngOnDestroy(): void {
    this.collaborationService.additionalDataPulledInTabs = {};
    this.socketService.unsubscribeFromTopics(this.socketService.allPostsSubscriptions);
  }

   addPost(data: {text:string, id:string}) {
    const text = data.text;
    this.collaborationService.addPost({
      text,
      html: text,
      spaceId: this.collaborationService.allPostSpaceDetails.spaceId,
      appId: this.collaborationService.allPostSpaceDetails.appId,
      userEmail: this.loggedInUser.email
    }).subscribe({
      next:  (res: any) => {
        if (res.data) {
          //this.handlePostsRefresh(res?.data?.posts);
          this.posts = [ this.getPostDataForRendering(res.data), ...this.posts];
          this.updateAppSubscribeStatus(true);    
          this.collaborationService.onMessageSuccessEvent.next({id:data.id,success:true});
        
        }
      },
      error: (err: any) => {        
        this.collaborationService.onMessageSuccessEvent.next({ id: data.id, success: false });
        this.collaborationService.displayErrorMessage(err,'Error while retrieving the data for post details request. Please try after some time.');
      }
    });
  }
  
  updateAppSubscribeStatus(subscribe:boolean) {
    this.subscribe = subscribe;
    this.collaborationService.allPostSpaceDetails.spaceFollowed = this.subscribe;
  }
  
  updatePostSpaceLevelSubscription() {
    this.collaborationService.updatePostSpaceLevelSubscription({
      spaceId:this.collaborationService.allPostSpaceDetails.spaceId,
      userId: this.loggedInUser.id,
     
    }).subscribe({
      next: (res: any) => {
        this.updateAppSubscribeStatus(!this.subscribe);        
      },
      error: (err: any) => {
        this.collaborationService.displayErrorMessage(err,'Error while subscribing. Please try after some time.');
      }
    })
    
  }

  backToPosts() {
    this.showPostDetails = false;
    this.selectedPost = null;
  }

  trackItemById(index: number, item: Post): any {
    return item.postId; // Assuming items have a unique "postId" property
  }

   handelAllTopicEvents(topicEvents: any) {
    switch (topicEvents.type) {
      case "allMessages":
        this.posts =  PostsData.map(post => this.getPostDataForRendering(post));
        //PostsData.map((post: Posts) => this.getPostDataForRendering(post))
        //JSON.parse(topicEvents.result.body).map((post: Posts) => this.getPostDataForRendering(post));
        this.showLoader = false;
        break;
      case "newMessage":
        const newData: Post = JSON.parse(topicEvents.result.body);
        newData.messageToDisplay = this.sanitizer.bypassSecurityTrustHtml(newData.text);
        this.posts.unshift(newData);
        break;
      case "editPost":
      case "newComments":
      case "editComment":
        const postData: Post =  this.getPostDataForRendering(JSON.parse(topicEvents.result.body));
        const postId = this.posts.findIndex(post => post.postId === postData.postId);
        if (postId !== -1) {
          postData.openCommentBox = this.posts[postId].openCommentBox;
          this.posts[postId] = postData;
        }
        if (this.selectedPost && this.selectedPost.id === postData.postId) {
          this.selectedPost = postData;
        }
        break;
    }
  }
  postDetailsView(post: Post) {
    this.showPostDetails = true
    this.selectedPost = post
  }

   getPostDataForRendering(post: Post) {
    post.comments = post.comments ?? [];
    //post.profilePictureUrl = await lastValueFrom(this.collaborationService.getUserProfilePicture(post.createdByEmail));
    post.profilePictureUrl = assetUrl('images/user.svg');
    post.comments.forEach((comment: Comment) => {
          comment.messageToDisplay = this.sanitizer.bypassSecurityTrustHtml(comment.text);
          //comment.profilePictureUrl = await lastValueFrom(this.collaborationService.getUserProfilePicture(comment.createdByEmail))
          comment.profilePictureUrl = assetUrl('images/user.svg');          
    });
    const numberOfComments = post.comments.length;
    return {
      ...post,
      messageToDisplay: this.sanitizer.bypassSecurityTrustHtml(post.text),
      openCommentBox: numberOfComments > 0 ? true : false,
      numberOfComments,
      comments: post.comments
    }
  }
  
  downloadPosts() {
    this.showLoader = true;
    this.collaborationService.downloadPosts()
      .subscribe({
        next:(res: any) => {
          this.collaborationService.doDownload(`${this.weavyData.appName}-posts`, "application/pdf", res);
          this.showLoader = false;
     
          (window as any).dataLayer.push({
            event: "referral_file_download",
            eventLabel: 'Download Posts',
            fileName: `${this.weavyData.appName}-posts.pdf`,
            tabName:'Post comments',
            eventPage: "Referral Actions",
          });
        },
        error: (err:any) => {
          this.showLoader = false;
          this.collaborationService.displayErrorMessage(err,'Error while downloading the posts. Please try after some time.');       
        }
      });
  }

   handlePostsRefresh(postsData: Post[]) {
    this.showLoader = true;
    this.posts = postsData.map(post => this.getPostDataForRendering(post));
    this.showLoader = false;
  }

  handleFullScreen() {
    (window as any).dataLayer.push({
      event: "referral_link_click",
      eventLabel: this.isFullScreen ? "Minimize" : "Full Screen",
      tabName:'Post comments',
      eventPage: "Referral Actions",
    });
    this.isFullScreen = !this.isFullScreen;
  }  

  notificationSettings() {
    (window as any).dataLayer.push({
      event: "referral_link_click",
      eventLabel: "Notification Settings",
      tabName: 'Post comments',
      eventPage: "Referral Actions",
    });
    this.collaborationService.setShowOrHideSideNav('notificationDrawer', true);
  }

  downloadPostsAndPolicyDocs() {
    this.downloadAdditionalDocs.emit(true);
    if(this.weavyData.isIhubScreen) {
      this.downloadPostsAndPolicyDocsIhub();
    } else{
      this.downloadPostsAndPolicyDocsReferral();
    }
  }

  downloadPostsAndPolicyDocsReferral() {
    this.showLoader = true;
    this.collaborationService.getActivityId(this.weavyData.trackerReferenceNumber).subscribe({
      next: (res) => {       
        if (res.data) {         
          const payload: any = {
            activityIds: res.data.referralDocumentIds.join()
          }
          if (res.data.dynamicsPolicyTermId) {
            payload["hubPolicyTermId"] = res.data.dynamicsPolicyTermId;
          }
          this.collaborationService.downloadPostsAndPolicyDocsReferral(this.weavyData.trackerReferenceNumber, payload)
            .subscribe({
              next: (res: any) => {
                this.showLoader = false;
                const fileName = this.weavyData.spaceName;
                const dataType = "application/zip"
                this.collaborationService.doDownload(fileName, dataType, res);
                (window as any).dataLayer.push({
                  event: "referral_link_click",
                  eventLabel: 'Download Policy Docs & Posts',
                  tabName: 'Post comments',
                  eventPage: "Referral Actions",
                });
              },
              error: (err: any) => {
                this.showLoader = false;
                this.collaborationService.displayErrorMessage(err, 'Error while downloading the policy docs and posts. Please try after some time.');
              }
            })

        }
      },
      error: (err:any) => {
        this.showLoader = false;
        this.collaborationService.displayErrorMessage(err, 'Error while getting the activity docs. Please try after some time.');
      }
    });
  }

  downloadPostsAndPolicyDocsIhub() {
    this.showLoader = true;
    this.collaborationService.downloadPostsAndPolicyDocsIhub(this.weavyData.spaceKey)
      .subscribe({
        next: (res: any) => {
          this.showLoader = false;
          const fileName = this.weavyData.spaceName;
          const dataType = "application/zip"
          this.collaborationService.doDownload(fileName, dataType, res);
          (window as any).dataLayer.push({
            event: "referral_link_click",
            eventLabel: 'Download All',
            tabName: 'Post comments',
            eventPage: "Referral Actions",
          });
        },
        error: (err: any) => {
          this.showLoader = false;
          this.collaborationService.displayErrorMessage(err, 'Error while downloading the Files. Please try after some time.');
        }
      })
  }

  override refreshWeavy(): void {
    this.refreshPosts(true);
  }
}



//const spaceId = window.location.href.split("spaceId=")[1] ?? "66bca7c96a4c4c744b4ef92d";
    //this.socketService.spaceId = spaceId;
    //this.listenToSocketConnection();
    // this.renderer.listen(
    //   'window',
    //   'collaborationPayloadData',
    //   (event: CustomEvent) => {
    //     this.zone.run(() => {
    //       this.socketService.spaceId = event.detail.payload.spaceId;
    //       this.listenToSocketConnection();
    //     });
    //   }
    // );
    // this.snackBar.openFromComponent(SnackbarComponent, {
    //   panelClass: 'error-snackbar',
    //   duration: 600000,
    //   data: {
    //     snackBar: this.snackBar,
    //     message: `Document was restored.`,
    //   },
    // });    
    

    // this.socketService.allPosts.subscribe(res => {
    //   this.handelAllTopicEvents(res);
    // });
    // window.addEventListener('beforeunload', () => {
    //   this.socketService.unsubscribeFromTopics(this.socketService.allPostsSubscriptions);
    // });